import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Twitter, Linkedin, Youtube, Send, Shield, Lock, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const footerSections = [
  {
    title: "Sections",
    links: [
      { label: "All News", href: "/news" },
      { label: "Brokers", href: "/category/brokers" },
      { label: "Prop Firms", href: "/category/prop-firms" },
      { label: "Exposés", href: "/category/exposes" },
      { label: "Regulation", href: "/category/regulation" },
      { label: "Reviews", href: "/category/reviews" },
      { label: "Fintech", href: "/category/fintech" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "About Us", href: "/about" },
      { label: "Contact", href: "/contact" },
      { label: "Advertising", href: "/advertise" },
      { label: "Media Kit", href: "/media-kit" },
      { label: "Editorial Policy", href: "/editorial-policy" },
      { label: "Write for Us", href: "/contribute" },
    ],
  },
  {
    title: "Legal & Compliance",
    links: [
      { label: "Terms of Service", href: "/terms" },
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Cookie Policy", href: "/cookies" },
      { label: "Disclaimer", href: "/disclaimer" },
      { label: "GDPR Compliance", href: "/gdpr" },
      { label: "Sitemap", href: "/sitemap.xml", external: true },
    ],
  },
];

const socialLinks = [
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Send, href: "#", label: "Telegram" },
  { icon: Youtube, href: "#", label: "YouTube" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    const { error } = await supabase.from("newsletter_subscribers").insert({
      email: email.trim(),
      source: "footer",
    });
    setSubscribing(false);
    if (error) {
      if (error.code === "23505") {
        toast.info("You're already subscribed!");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
      return;
    }
    toast.success("Subscribed! Welcome to ForexWireDaily Insider.");
    setEmail("");
  };

  return (
    <footer className="border-t border-secondary/20 mt-12" style={{ background: "rgba(10, 12, 16, 0.9)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
      {/* Newsletter bar */}
      <div className="border-b border-border/50">
        <div className="container py-6 flex flex-col md:flex-row items-center gap-4 justify-between">
          <div>
            <h3 className="font-display text-sm font-semibold">Stay Ahead of the Wire</h3>
            <p className="text-xs text-muted-foreground">Get breaking forex & fintech news delivered to your inbox.</p>
          </div>
          <form className="flex items-center gap-2 w-full md:w-auto" onSubmit={handleSubscribe}>
            <Input
              type="email"
              placeholder="your@email.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9 w-full md:w-64 bg-muted/50 border-border text-sm"
            />
            <Button size="sm" className="shrink-0 font-display font-semibold" disabled={subscribing}>
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              {subscribing ? "..." : "Subscribe"}
            </Button>
          </form>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="container py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-2">
            <Logo variant="footer" className="mb-3" />
            <p className="text-xs font-display text-primary mb-1">Forex · Fintech · Prop Trading</p>
            <p className="text-sm text-muted-foreground mb-4 max-w-xs">
              The Pulse of Forex, Fintech & Prop Trading — your daily source for prop firm news, broker intelligence, and regulatory alerts.
            </p>
            <div className="flex items-center gap-2 mb-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="h-8 w-8 rounded-md bg-muted/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            {/* Trust badges */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground border border-border/50 rounded px-2 py-1">
                <Lock className="h-3 w-3" /> SSL Secure
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground border border-border/50 rounded px-2 py-1">
                <Shield className="h-3 w-3" /> Google News
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground border border-border/50 rounded px-2 py-1">
                Est. 2025
              </span>
            </div>
          </div>

          {/* Link columns */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h4 className="font-display text-xs font-semibold uppercase tracking-wider text-foreground mb-3">
                {section.title}
              </h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {"external" in link && link.external ? (
                      <a
                        href={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sitemap`}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1 group"
                      >
                        {link.label}
                        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1 group"
                      >
                        {link.label}
                        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Risk warning & copyright */}
      <div className="border-t border-border/50">
        <div className="container py-4 space-y-2">
          <p className="text-[10px] text-muted-foreground/70 leading-relaxed">
            <strong className="text-muted-foreground">Risk Warning:</strong> Trading leveraged products such as Forex and CFDs involves substantial risk of loss and is not suitable for all investors. Past performance is not indicative of future results. Please read our full disclaimer before trading.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-[10px] text-muted-foreground/60">
            <span>© 2025 ForexWireDaily.com. All rights reserved.</span>
            <span>Market data provided for informational purposes only.</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
